import React, { useEffect, useRef } from "react";
import styles from "../ChatTestPage.module.scss";
import { ROUTES } from "../../../constants/routes";
import Footer from "../../../components/footer/Footer";
import Header from "../../../components/header/Header";
import MovieCarouselMobile from "./MovieCarouselMobile";
import GradientBackdrop from "../../../components/shared/GradientBackdrop/GradientBackdrop";
import ArrowUp from "../../../components/icons/chat/arrowUp";
import TitleIcon from "../../../components/icons/chat/titleIcon";
import { useChatTestPage } from "./useChatTestPage";
import ChatMessagesList from "./ChatMessagesList";

const ChatTestPageMobile: React.FC = () => {
  const {
    t,
    messages,
    input,
    setInput,
    loading,
    sendMessage,
    stopMessage,
    inputRef,
    handleKeyDown,
    hasMovies,
    hasStarted,
    lastMoviesIndex,
    moviesAreVisible,
    shouldShowOverlay,
    isChatExpanded,
    setIsChatExpanded,
    carouselPropsBase,
    openLoginModal,
    mobileChatMessagesRef,
    modals,
  } = useChatTestPage();

  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isChatExpanded) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) {
        setIsChatExpanded(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isChatExpanded, setIsChatExpanded]);

  const inputBarMobile = (
    <>
      {loading && (
        <div className={`${styles.message} ${styles.assistantMessage}`}>
          <div className={styles.fadeIn}>
            <div className={styles.messageWrapper}>
              <div className={styles.messageContent}>
                <div className={styles.assistantTyping}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.inputWrapper}>
        <textarea
          ref={inputRef}
          className={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("chat_placeholder", "What would you like to watch?")}
          disabled={loading}
          onFocus={() => {
            if (moviesAreVisible) setIsChatExpanded(true);
          }}
          maxLength={2000}
        />
        <button
          className={styles.sendBtn}
          onClick={loading ? stopMessage : sendMessage}
          disabled={!loading && !input.trim()}
        >
          {loading ? <span className={styles.stopIcon} /> : <ArrowUp />}
        </button>
        <span className={styles.textWrap}>Ai</span>
      </div>
    </>
  );

  return (
    <div className={styles.page}>
      <div className={styles.pageContainer}>
        <Header source={ROUTES.GID} clickHandler={() => {}} className={styles.headerWrapperFilters} />

        <GradientBackdrop />
        <div className={styles.chatArea}>
          <div className={`${styles.chatSectionStarted} ${hasStarted ? styles.chatSectionStartedFull : ""}`}>
            <div className={styles.spacerTop} />

            {hasMovies ? (
              <div
                className={`${styles.chatSection} ${styles.chatSectionWithMovies} ${styles.chatSectionMoviesFull}`}
              >
                <div className={`${styles.moviesList} ${styles.fadeIn}`}>
                  <MovieCarouselMobile
                    movies={messages[lastMoviesIndex].movies}
                    {...carouselPropsBase}
                    isMobile={true}
                    openLoginModal={openLoginModal}
                  />
                </div>

                {/* One stable container: the class toggles between the collapsed
                    in-flow input and the absolute overlay sheet, but the input
                    (inside .chatWrapperInput) always stays the same DOM node so
                    focus and the keyboard are not lost when it expands. */}
                <div
                  ref={sheetRef}
                  className={`${shouldShowOverlay ? styles.chatOverlaySheet : styles.chatWrapperInputOverlay} ${styles.fadeIn}`}
                >
                  <div className={styles.chatMobileHeader}>
                    <div className={styles.chatColumnHeader}>
                      <TitleIcon />
                      <span>{t("chat_gid", "Guide")}</span>
                    </div>

                    <span className={styles.textWrap}>Ai</span>
                  </div>

                  <div className={styles.chatMessages} ref={mobileChatMessagesRef}>
                    <ChatMessagesList messages={messages} isMobile={true}/>
                  </div>

                  <div className={styles.chatWrapperInput}>{inputBarMobile}</div>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.chatSection}>
                  {messages.length === 0 && (
                    <div className={`${styles.chatWrapper} ${styles.fadeIn}`}>
                      <div className={styles.welcome}>
                        <h2>{t("chat_welcome_title", "Hello, Viewer!")}</h2>
                        <p>{t("chat_welcome_subtitle", "I'm your personal guide")}</p>
                      </div>
                    </div>
                  )}
                  {hasStarted && (
                    <div className={styles.fadeIn}>
                      <div className={styles.chatHeading}>
                        <TitleIcon />
                        <span>{t("chat_gid", "Guide")}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`${styles.chatSectionMessageWrap} ${styles.chatSection} ${loading ? styles.chatSectionWithMovies : ""}`}>
                  {messages.map((msg) => {
                    if (!msg.content) return null;

                    return (
                      <div
                        key={msg.id}
                        className={`${styles.message} ${
                          msg.role === "user" ? styles.userMessage : styles.assistantMessage
                        }`}
                      >
                        <div className={styles.fadeIn}>
                          <div className={styles.messageWrapper}>
                            <div className={styles.messageContent}>{msg.content}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className={styles.spacerMiddle} />
                <div className={`${styles.chatWrapperInput} ${styles.fadeIn}`}>{inputBarMobile}</div>
              </>
            )}

            <div className={styles.spacerBottom} />
          </div>
        </div>

        {modals}

        <Footer source={""} />
      </div>
    </div>
  );
};

export default ChatTestPageMobile;
